import React, { useEffect, useState } from 'react';
import { BellRing, CalendarCheck, FileText, IndianRupee, MessageCircle, Users } from 'lucide-react';

// What happens in an institute's day on CoachHub, as a looping picture beside the login form.
// Illustration only: the figures are sample values, not anyone's data.
const EVENTS = [
  { icon: CalendarCheck, tone: 'bg-emerald-500', title: 'Attendance marked', detail: 'Class 10 A · 38 of 40 present' },
  { icon: IndianRupee, tone: 'bg-amber-500', title: 'Fee received', detail: '₹12,000 · receipt sent on WhatsApp' },
  { icon: FileText, tone: 'bg-indigo-500', title: 'Report cards ready', detail: 'Term 1 · 240 students' },
  { icon: MessageCircle, tone: 'bg-green-500', title: 'Reminder sent', detail: 'Fee due tomorrow · 42 parents' },
];

const BARS = [46, 62, 54, 78, 70, 88, 82];

function useReducedMotion() {
  const [reduce] = useState(() => typeof window !== 'undefined' && Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches));
  return reduce;
}

export default function AuthShowcase() {
  const reduce = useReducedMotion();
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (reduce) return undefined;
    const timer = setInterval(() => setTick((t) => t + 1), 2600);
    return () => clearInterval(timer);
  }, [reduce]);

  const event = EVENTS[tick % EVENTS.length];
  const previous = EVENTS[(tick + EVENTS.length - 1) % EVENTS.length];
  const EventIcon = event.icon;
  const PrevIcon = previous.icon;

  return (
    <div className="relative mx-auto w-full max-w-md select-none" aria-hidden="true" data-testid="auth-showcase">
      <style>{`
        @keyframes auth-rise { from { transform: scaleY(0.25); } to { transform: scaleY(1); } }
        @keyframes auth-in { from { opacity: 0; transform: translateY(14px) scale(0.97); } to { opacity: 1; transform: none; } }
        @keyframes auth-float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
        .auth-bar { transform-origin: bottom; animation: auth-rise 1.4s cubic-bezier(.2,.8,.2,1) both; }
        .auth-toast { animation: auth-in .5s cubic-bezier(.2,.8,.2,1) both; }
        .auth-float { animation: auth-float 5s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) { .auth-bar, .auth-toast, .auth-float { animation: none; } }
      `}</style>

      {/* Dashboard card */}
      <div className="auth-float rounded-3xl border border-white/15 bg-white/10 p-5 shadow-2xl shadow-black/30 backdrop-blur-md">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-rose-400/80" />
            <span className="h-2.5 w-2.5 rounded-full bg-amber-300/80" />
            <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/80" />
          </div>
          <span className="flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-1 text-[10px] font-semibold text-indigo-100">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 motion-safe:animate-pulse" /> Live today
          </span>
        </div>

        <div className="grid grid-cols-3 gap-2.5">
          {[
            { icon: Users, label: 'Students', value: '1,248' },
            { icon: CalendarCheck, label: 'Present', value: '96%' },
            { icon: IndianRupee, label: 'Fees today', value: '₹48K' },
          ].map(({ icon: Icon, label, value }) => (
            <div key={label} className="rounded-2xl bg-white/10 p-3">
              <Icon className="h-4 w-4 text-brand-gold" />
              <p className="mt-2 font-cabinet text-lg font-bold leading-none text-white">{value}</p>
              <p className="mt-1 text-[10px] text-indigo-200/80">{label}</p>
            </div>
          ))}
        </div>

        <div className="mt-4 rounded-2xl bg-white/5 p-3">
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-indigo-200/80">Fee collection · this week</p>
          <div className="flex h-24 items-end gap-2" key={reduce ? 'still' : Math.floor(tick / EVENTS.length)}>
            {BARS.map((height, i) => (
              <span
                key={i}
                className="auth-bar flex-1 rounded-t-md bg-gradient-to-t from-indigo-500 to-sky-400"
                style={{ height: `${height}%`, animationDelay: `${i * 90}ms` }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Notifications: the newest slides in, the one before it fades behind */}
      <div className="relative -mt-6 ml-auto mr-[-1rem] h-[4.5rem] w-[85%]">
        <div className="absolute inset-x-3 top-2 flex items-center gap-3 rounded-2xl bg-white/70 p-3 opacity-60 shadow-lg dark:bg-slate-800/70">
          <span className={`flex h-8 w-8 items-center justify-center rounded-xl text-white ${previous.tone}`}><PrevIcon className="h-4 w-4" /></span>
          <span className="truncate text-xs font-semibold text-slate-700 dark:text-slate-200">{previous.title}</span>
        </div>
        <div key={tick} className="auth-toast absolute inset-x-0 top-0 flex items-center gap-3 rounded-2xl bg-white p-3 shadow-xl dark:bg-slate-900">
          <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-white ${event.tone}`}><EventIcon className="h-4 w-4" /></span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-bold text-slate-900 dark:text-white">{event.title}</span>
            <span className="block truncate text-xs text-slate-500 dark:text-slate-400">{event.detail}</span>
          </span>
          <BellRing className="h-4 w-4 shrink-0 text-indigo-500" />
        </div>
      </div>
    </div>
  );
}
